import React from 'react';
import { cn } from '@/lib/utils';
import { config } from '@/config';
import {
  effectiveCommentaireTheme,
  tutorialStepBubbleShellClassList,
  tutorialStepBubbleTextClassList,
  tutorialStepBubbleTextStyle,
} from '@/lib/videoTutorialCommentTheme';
import { COMMENT_STYLES } from '@/lib/videoTutorialCommentStyles';

export function hasValidTutorialScreenshot(url) {
  if (typeof url !== 'string' || !url) return false;
  if (url.startsWith('data:image')) return url.length > 100;
  if (url.startsWith('http://') || url.startsWith('https://')) return url.length > 10;
  // Fichier physique migré : /api/files/tut_...
  if (url.startsWith('/api/files/')) return true;
  return false;
}

/**
 * Résout une URL de screenshot pour affichage / vérification.
 * - API relative (/api) : laisser /api/files/... (proxy nginx même origine).
 * - API absolue (Coolify : api-app…/api) : pointer vers l'hôte API, pas le frontend.
 */
export function resolveScreenshotUrl(url) {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  if (!url.startsWith('/api/files/')) return url;
  const apiBase = String(config.BACKEND_URL || '/api').replace(/\/+$/, '');
  if (/^https?:\/\//i.test(apiBase)) {
    const origin = apiBase.replace(/\/api$/i, '');
    return `${origin}${url}`;
  }
  return url;
}

/** Choisit la meilleure source d'affichage pour une étape (fichier ou base64). */
export function pickScreenshotSrc(comment, { preferFlattened = false } = {}) {
  if (!comment) return '';
  const flat = comment.screenshot_url;
  const raw = comment.screenshot_raw;
  const useFlat = preferFlattened && comment.screenshot_flattened === true && hasValidTutorialScreenshot(flat);
  if (useFlat) return resolveScreenshotUrl(flat);
  if (hasValidTutorialScreenshot(raw)) return resolveScreenshotUrl(raw);
  if (hasValidTutorialScreenshot(flat)) return resolveScreenshotUrl(flat);
  return '';
}

/** Capture brute uniquement — pour recomposer l'image finale sans empiler le texte déjà aplati. */
export function pickRawScreenshotSrc(comment) {
  if (!comment) return '';
  if (hasValidTutorialScreenshot(comment.screenshot_raw)) {
    return resolveScreenshotUrl(comment.screenshot_raw);
  }
  if (hasValidTutorialScreenshot(comment.screenshot_url) && comment.screenshot_flattened !== true) {
    return resolveScreenshotUrl(comment.screenshot_url);
  }
  return '';
}

/** Source de repli si la première URL échoue au chargement (hors URLs déjà essayées). */
export function pickScreenshotFallbackSrc(comment, failedSrc, tried = new Set()) {
  if (!comment) return '';
  const candidates = [comment.screenshot_url, comment.screenshot_raw]
    .filter(hasValidTutorialScreenshot)
    .map(resolveScreenshotUrl);
  const unique = [...new Set(candidates)];
  if (failedSrc) tried.add(failedSrc);
  return unique.find((u) => u && !tried.has(u)) || '';
}

/** Vérifie qu'un fichier /api/files/... est accessible sur le serveur API. */
export function verifyScreenshotFileUrl(url) {
  if (!url) return Promise.resolve(false);
  if (url.startsWith('data:image')) return Promise.resolve(true);
  if (!url.startsWith('/api/files/') && !url.startsWith('http')) return Promise.resolve(false);
  const resolved = resolveScreenshotUrl(url);
  return fetch(resolved, { method: 'HEAD', credentials: 'omit', cache: 'no-store' })
    .then((r) => r.ok)
    .catch(() => false);
}

/** Retire les références /api/files/ dont le fichier n'existe plus sur le serveur. */
export async function sanitizeCommentScreenshots(comments) {
  if (!Array.isArray(comments)) return [];
  return Promise.all(
    comments.map(async (c) => {
      if (!c || typeof c !== 'object') return c;
      const nc = { ...c };
      let changed = false;
      for (const field of ['screenshot_url', 'screenshot_raw']) {
        const v = nc[field];
        if (typeof v === 'string' && v.startsWith('/api/files/')) {
          const ok = await verifyScreenshotFileUrl(v);
          if (!ok) {
            delete nc[field];
            changed = true;
          }
        }
      }
      if (changed && nc.screenshot_flattened) nc.screenshot_flattened = false;
      return nc;
    })
  );
}

function TutorialScreenshotImage({ comment, preferFlattened, alt, className }) {
  const failedUrlsRef = React.useRef(new Set());
  const [src, setSrc] = React.useState(() => pickScreenshotSrc(comment, { preferFlattened }));

  React.useEffect(() => {
    failedUrlsRef.current = new Set();
    setSrc(pickScreenshotSrc(comment, { preferFlattened }));
  }, [comment?.screenshot_url, comment?.screenshot_raw, comment?.screenshot_flattened, preferFlattened]);

  const handleError = React.useCallback(() => {
    setSrc((current) => {
      if (!current) return '';
      failedUrlsRef.current.add(current);
      return pickScreenshotFallbackSrc(comment, current, failedUrlsRef.current) || '';
    });
  }, [comment?.screenshot_url, comment?.screenshot_raw, comment?.screenshot_flattened]);

  if (!src) return null;
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={handleError}
    />
  );
}

/**
 * Image aplatie parfois invalide (canvas noir, bug de mapping % → pixels).
 * Si la chaîne aplatie est très petite par rapport à la capture brute, on ignore le flag.
 */
export function shouldUseFlattenedComposite(c) {
  if (!c || c.screenshot_flattened !== true) return false;
  const flat = c.screenshot_url;
  const raw = c.screenshot_raw;
  if (!hasValidTutorialScreenshot(flat)) return false;
  if (
    hasValidTutorialScreenshot(raw) &&
    raw.length > 80000 &&
    flat.length < Math.floor(raw.length * 0.3)
  ) {
    return false;
  }
  return true;
}

/** True si l’étape a une capture utilisable (même logique que l’ancien StepsPlayer). */
export function stepHasUsableScreenshot(currentStep) {
  return !!currentStep?.comments?.find(
    (c) => hasValidTutorialScreenshot(c.screenshot_url) || hasValidTutorialScreenshot(c.screenshot_raw)
  );
}

function getAnchorPosition(comment, anchorType, anchorKey) {
  const ANCHOR_POINTS = {
    'top-left': { x: 0, y: 0 },
    top: { x: 50, y: 0 },
    'top-right': { x: 100, y: 0 },
    left: { x: 0, y: 50 },
    center: { x: 50, y: 50 },
    right: { x: 100, y: 50 },
    'bottom-left': { x: 0, y: 100 },
    bottom: { x: 50, y: 100 },
    'bottom-right': { x: 100, y: 100 },
  };

  const anchor = ANCHOR_POINTS[anchorKey || 'center'];

  if (anchorType === 'comment') {
    const widthPercent = comment.largeur ?? 18;
    const heightPercent = comment.hauteur ?? 10;
    const halfW = widthPercent / 2;
    const halfH = heightPercent / 2;
    return {
      x: comment.position_x - halfW + (widthPercent * anchor.x) / 100,
      y: comment.position_y - halfH + (heightPercent * anchor.y) / 100,
    };
  }
  if (!comment.zone_cible) return { x: 50, y: 50 };
  const ox = comment.zone_cible_offset_x ?? 0;
  const oy = comment.zone_cible_offset_y ?? 0;
  return {
    x: comment.zone_cible.x + ox + (comment.zone_cible.largeur * anchor.x) / 100,
    y: comment.zone_cible.y + oy + (comment.zone_cible.hauteur * anchor.y) / 100,
  };
}

function renderSpotlightAndComments({
  tutorial,
  currentStep,
  currentStepIndex,
  useScreenshotFlattened,
}) {
  if (!currentStep?.comments?.length) return null;
  return (
    <>
      {!useScreenshotFlattened &&
        currentStep.comments.some((c) => c.zone_cible || c.spotlight_on !== false) && (
          <svg
            className="absolute inset-0 h-full w-full pointer-events-none"
            style={{ zIndex: 15 }}
          >
            <defs>
              <mask id={`steps-mask-${currentStepIndex}`}>
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                {currentStep.comments.map((comment, idx) => {
                  const hasZone = comment.zone_cible;
                  const useCommentZone = !hasZone && comment.spotlight_on !== false;
                  if (!hasZone && !useCommentZone) return null;
                  const ox = (hasZone ? comment.zone_cible_offset_x : 0) ?? 0;
                  const oy = (hasZone ? comment.zone_cible_offset_y : 0) ?? 0;
                  const zx = hasZone
                    ? comment.zone_cible.x + ox
                    : (comment.position_x ?? 50) - (comment.largeur ?? 18) / 2;
                  const zy = hasZone
                    ? comment.zone_cible.y + oy
                    : (comment.position_y ?? 50) - (comment.hauteur ?? 10) / 2;
                  const zl = hasZone ? comment.zone_cible.largeur : comment.largeur ?? 18;
                  const zh = hasZone ? comment.zone_cible.hauteur : comment.hauteur ?? 10;
                  const isEllipse = hasZone && (comment.zone_cible.forme || 'rectangle') === 'ellipse';
                  return isEllipse ? (
                    <ellipse
                      key={idx}
                      cx={`${zx + zl / 2}%`}
                      cy={`${zy + zh / 2}%`}
                      rx={`${zl / 2}%`}
                      ry={`${zh / 2}%`}
                      fill="black"
                    />
                  ) : (
                    <rect
                      key={idx}
                      x={`${zx}%`}
                      y={`${zy}%`}
                      width={`${zl}%`}
                      height={`${zh}%`}
                      fill="black"
                    />
                  );
                })}
              </mask>
            </defs>
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill={`rgba(0,0,0,${
                (currentStep.comments.find((c) => c.zone_cible || c.spotlight_on !== false)
                  ?.spotlight_opacity ??
                currentStep.comments.find((c) => c.zone_cible)?.zone_cible?.opacity ??
                100) / 100
              })`}
              mask={`url(#steps-mask-${currentStepIndex})`}
            />
            {currentStep.comments
              .filter((c) => c.zone_cible || (c.spotlight_on !== false && !c.zone_cible))
              .map((comment, idx) => {
                const hasZone = !!comment.zone_cible;
                const ox = (hasZone ? comment.zone_cible_offset_x : 0) ?? 0;
                const oy = (hasZone ? comment.zone_cible_offset_y : 0) ?? 0;
                const zx = hasZone
                  ? comment.zone_cible.x + ox
                  : (comment.position_x ?? 50) - (comment.largeur ?? 18) / 2;
                const zy = hasZone
                  ? comment.zone_cible.y + oy
                  : (comment.position_y ?? 50) - (comment.hauteur ?? 10) / 2;
                const zl = hasZone ? comment.zone_cible.largeur : comment.largeur ?? 18;
                const zh = hasZone ? comment.zone_cible.hauteur : comment.hauteur ?? 10;
                const isEllipse = hasZone && (comment.zone_cible.forme || 'rectangle') === 'ellipse';
                return isEllipse ? (
                  <ellipse
                    key={`border-${idx}`}
                    cx={`${zx + zl / 2}%`}
                    cy={`${zy + zh / 2}%`}
                    rx={`${zl / 2}%`}
                    ry={`${zh / 2}%`}
                    fill="none"
                    stroke={COMMENT_STYLES[comment.type]?.hex || '#3B82F6'}
                    strokeWidth="2"
                  />
                ) : (
                  <rect
                    key={`border-${idx}`}
                    x={`${zx}%`}
                    y={`${zy}%`}
                    width={`${zl}%`}
                    height={`${zh}%`}
                    fill="none"
                    stroke={COMMENT_STYLES[comment.type]?.hex || '#3B82F6'}
                    strokeWidth="2"
                  />
                );
              })}
            {currentStep.comments
              .filter((c) => c.zone_cible && c.afficher_liaison)
              .map((comment, idx) => {
                const commentPos = getAnchorPosition(comment, 'comment', comment.ancre_commentaire);
                const zonePos = getAnchorPosition(comment, 'zone', comment.ancre_zone);
                return (
                  <g key={`line-${idx}`}>
                    <line
                      x1={`${commentPos.x}%`}
                      y1={`${commentPos.y}%`}
                      x2={`${zonePos.x}%`}
                      y2={`${zonePos.y}%`}
                      stroke={COMMENT_STYLES[comment.type]?.hex || '#3B82F6'}
                      strokeWidth="2"
                      strokeDasharray="4,4"
                    />
                    <circle
                      cx={`${zonePos.x}%`}
                      cy={`${zonePos.y}%`}
                      r="4"
                      fill={COMMENT_STYLES[comment.type]?.hex || '#3B82F6'}
                    />
                  </g>
                );
              })}
          </svg>
        )}
      {!useScreenshotFlattened &&
        currentStep.comments.map((comment, idx) => (
          <div
            key={comment.id ?? `comment-${idx}`}
            className={tutorialStepBubbleShellClassList(
              effectiveCommentaireTheme(comment, tutorial),
              COMMENT_STYLES[comment.type] || COMMENT_STYLES.info,
              { stable16: true }
            )}
            style={{
              left: `${comment.position_x ?? 50}%`,
              top: `${comment.position_y ?? 50}%`,
              transform: 'translate(-50%, -50%)',
              boxSizing: 'border-box',
              /* Largeur = contenu ; plafond quasi toute la scène (les % trop petits en base forçaient des coupures dans les mots) */
              width: 'max-content',
              maxWidth: '98%',
              height: 'auto',
              minHeight: 0,
              maxHeight: 'none',
              overflow: 'visible',
            }}
          >
            <p
              lang="fr"
              className={tutorialStepBubbleTextClassList(effectiveCommentaireTheme(comment, tutorial), {
                stable16: true,
              })}
              style={tutorialStepBubbleTextStyle(effectiveCommentaireTheme(comment, tutorial), {
                stable16: true,
                texteRenduScale: comment.texte_rendu_scale,
              })}
            >
              {(comment.texte || '').trimEnd()}
            </p>
          </div>
        ))}
    </>
  );
}

/**
 * Rendu unique d’une étape (capture + flous + spotlight + bulles + liaisons).
 * Scène 16:9 alignée sur l’éditeur pour le mode capture ; calques seuls pour la vidéo sans capture.
 *
 * @param {'screenshot' | 'videoOverlays'} mode
 * @param {'inline' | 'layoutCard'} layout
 */
export default function TutorialStepStage({
  tutorial,
  currentStep,
  currentStepIndex = 0,
  mode = 'screenshot',
  layout = 'layoutCard',
  fitViewport = false,
  externalizeStepMeta = false,
  compactLayout = true,
  alt,
}) {
  const firstCommentWithScreenshot = currentStep?.comments?.find(
    (c) => hasValidTutorialScreenshot(c.screenshot_url) || hasValidTutorialScreenshot(c.screenshot_raw)
  );
  const useScreenshot = !!(
    firstCommentWithScreenshot?.screenshot_url || firstCommentWithScreenshot?.screenshot_raw
  );
  const useScreenshotFlattened = shouldUseFlattenedComposite(firstCommentWithScreenshot);
  const backgroundImageSrc = pickScreenshotSrc(firstCommentWithScreenshot, {
    preferFlattened: useScreenshotFlattened,
  });

  const overlays = renderSpotlightAndComments({
    tutorial,
    currentStep,
    currentStepIndex,
    useScreenshotFlattened,
  });

  const blurLayer =
    useScreenshot &&
    backgroundImageSrc &&
    !useScreenshotFlattened &&
    (currentStep?.comments
      ?.flatMap((c) => (c.zones_flou || []).map((z, i) => ({ ...z, _key: z.id || `${c.id}-blur-${i}` }))) ?? []
    ).map((zone) => (
      <div
        key={zone._key || zone.id}
        className="absolute z-[12] pointer-events-none"
        style={{
          left: `${zone.x ?? 0}%`,
          top: `${zone.y ?? 0}%`,
          width: `${zone.largeur ?? 15}%`,
          height: `${zone.hauteur ?? 10}%`,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(255,255,255,0.05)',
        }}
      />
    ));

  if (mode === 'videoOverlays') {
    return (
      <div className="pointer-events-none absolute inset-0 z-[6]">{overlays}</div>
    );
  }

  if (!useScreenshot || !backgroundImageSrc) {
    return null;
  }

  const stageInner = (
    <>
      <TutorialScreenshotImage
        comment={firstCommentWithScreenshot}
        preferFlattened={useScreenshotFlattened}
        alt={alt || 'Étape tutoriel'}
        className="pointer-events-none absolute inset-0 z-10 h-full w-full object-contain"
      />
      {blurLayer}
      {overlays}
    </>
  );

  if (layout === 'inline') {
    return (
      <div className="absolute inset-0 z-[5] flex items-center justify-center bg-black">
        <div className="relative aspect-video max-h-full w-full max-w-full overflow-hidden bg-black">
          {stageInner}
        </div>
      </div>
    );
  }

  if (layout === 'layoutCard' && fitViewport && externalizeStepMeta) {
    return (
      <div className="relative flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden bg-black">
        <div className="relative aspect-video h-auto max-h-full w-full max-w-full overflow-hidden">
          {stageInner}
        </div>
      </div>
    );
  }
  return (
    <div
      className={cn(
        'relative mx-auto aspect-video w-full max-w-full overflow-hidden bg-black',
        fitViewport && !externalizeStepMeta && 'max-h-[min(52dvh,calc(100dvh-22rem))]',
        !fitViewport && compactLayout && 'max-h-[40vh]',
        !fitViewport && !compactLayout && 'min-h-[200px]'
      )}
    >
      {stageInner}
    </div>
  );
}
